import React from "react";
import { FaCircle, FaMapMarkerAlt, FaCheckCircle, FaBox, FaTruck, FaShippingFast } from "react-icons/fa";

interface TrackingEvent {
  status: string;
  location?: string;
  timestamp: string | Date;
  description?: string;
  statusDescription?: string; // Backend uses this field name
}

interface TrackingTimelineProps {
  events: TrackingEvent[];
  currentStatus?: string;
}

export const TrackingTimeline: React.FC<TrackingTimelineProps> = ({ events }) => {
  if (!events || events.length === 0) {
    return null;
  }

  // Sort events by timestamp (most recent first)
  const sortedEvents = [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  // Get icon and color for status
  const getStatusIcon = (status: string, isLatest: boolean): React.ReactNode => {
    const statusLower = status.toLowerCase();
    
    if (isLatest) {
      if (statusLower.includes("delivered") || statusLower.includes("delivery")) {
        return <FaCheckCircle className="text-green-500" size={16} />;
      }
      if (statusLower.includes("transit") || statusLower.includes("shipped")) {
        return <FaTruck className="text-blue-500" size={16} />;
      }
      if (statusLower.includes("processing") || statusLower.includes("preparing")) {
        return <FaBox className="text-yellow-500" size={16} />;
      }
      return <FaCircle className="text-blue-500" size={16} />;
    }
    
    return <FaCircle className="text-gray-400" size={12} />;
  };

  const getStatusColor = (status: string, isLatest: boolean): string => {
    if (!isLatest) return "border-gray-200";
    
    const statusLower = status.toLowerCase();
    if (statusLower.includes("delivered") || statusLower.includes("delivery")) {
      return "border-green-500";
    }
    if (statusLower.includes("transit") || statusLower.includes("shipped")) {
      return "border-blue-500";
    }
    if (statusLower.includes("processing") || statusLower.includes("preparing")) {
      return "border-yellow-500";
    }
    return "border-blue-300";
  };

  const isLatestEvent = (index: number): boolean => {
    return index === 0;
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <FaShippingFast className="text-blue-500" size={14} />
        Tracking Timeline
      </h4>
      <div className="relative">
        {sortedEvents.map((event, index) => {
          const isLatest = isLatestEvent(index);
          const eventDate = new Date(event.timestamp);
          
          return (
            <div key={index} className="relative pl-6 pb-6 last:pb-0">
              {/* Timeline line */}
              {index < sortedEvents.length - 1 && (
                <div className={`absolute left-[7px] top-6 bottom-0 w-0.5 ${getStatusColor(event.status, isLatest)}`} />
              )}
              
              {/* Icon */}
              <div className={`absolute left-0 top-1 ${isLatest ? "bg-white" : ""}`}>
                {getStatusIcon(event.status, isLatest)}
              </div>
              
              {/* Content */}
              <div className={`${isLatest ? "bg-blue-50 border border-blue-200 rounded-lg p-3" : ""}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${isLatest ? "text-blue-900" : "text-gray-900"}`}>
                      {event.status}
                    </div>
                    {event.location && (
                      <div className="text-xs text-gray-600 mt-1 flex items-center gap-1">
                        <FaMapMarkerAlt size={10} />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {(event.description || event.statusDescription) && (
                      <div className="text-xs text-gray-500 mt-1">
                        {event.description || event.statusDescription}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                    {eventDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    })}
                    <br />
                    <span className="text-xs">
                      {eventDate.toLocaleTimeString("en-US", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
